import type { SyntheticEvent } from 'react'
import { RemoteEventHandlerResponse } from '../types'
import { serializeEvent } from './serialize'

interface RetriggerableEvent extends Event {
	__retriggered?: boolean
}

const PREVENT_DEFAULT_SUPPORTED_EVENTS = ['click', 'submit']

const processProps = (props: Record<string, any> | null | undefined) =>
	props &&
	Object.entries(props).reduce<Record<string, any>>((acc, [key, val]) => {
		if (typeof val === 'function') {
			acc[key] = async (...args: any) => {
				const [firstArg, ...restArgs] = args
				const isDomEvent = typeof firstArg === 'object' && firstArg.target

				if (isDomEvent) {
					const eventHandler = val
					const event: Event | SyntheticEvent = firstArg

					if ('persist' in event) {
						event.persist()
					}

					const nativeEvent: RetriggerableEvent = 'nativeEvent' in event ? event.nativeEvent : event

					if (nativeEvent.__retriggered) {
						// This means the event was created by the user, not by the browser.
						// It's retriggered event after preventDefault
						// Ignore it because event handler was already called the first time
						return
					}

					const preventDefaultSupported =
						nativeEvent.cancelable && PREVENT_DEFAULT_SUPPORTED_EVENTS.includes(event.type)

					if (preventDefaultSupported) {
						event.preventDefault()
					}

					const { returnValue, defaultPrevented }: RemoteEventHandlerResponse = await eventHandler(
						serializeEvent(event),
						...restArgs
					)

					if (preventDefaultSupported && !defaultPrevented) {
						triggerDefaultEventBehavior(nativeEvent)
					}

					return returnValue
				}

				return val(...args)
			}
		} else {
			// todo - use DOMPurify.sanitize (like in the PR)
			acc[key] = val
		}
		return acc
	}, {})

const triggerDefaultEventBehavior = (event: Event) => {
	switch (event.type) {
		case 'click': {
			const originalEvent = event as MouseEvent
			const newEvent = new MouseEvent('click', originalEvent)
			Object.defineProperty(newEvent, '__retriggered', { value: true })
			originalEvent.target?.dispatchEvent(newEvent)
			break
		}
		case 'submit': {
			const target = (event as Event).target as HTMLFormElement | null
			target?.submit()
			break
		}
	}
}

export default processProps
